import React, { useCallback, useEffect } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { FormikActions } from 'formik'
import { Box, Flex, TextBox } from '@revolut/ui-kit'

import { FetchStates, Screens } from 'constants/common'
import { PushNotificationToken } from 'types/common'
import { AppState } from 'store'
import { authenticateAction, fetchPushNotificationTokenAction } from 'thunks'
import { RetailAuthenticationBody, RetailFetchTokenBody } from 'services/api'
import { setActiveScreenAction } from 'reducers/generalReducer'
import { useCountdown } from 'hooks/useCountdown'
import { Slide } from 'components/Slide'
import { CountdownCircle } from 'components/CountdownCircle'
import { DifferentMethod } from './DifferentMethod'

const TOKEN_REFETCH_INTERVAL_MS = 2000
const MIN_APP_VERSION = '7.23'

type Phone = {
  code: string
  number: string
}

const noop = () => {}

type SlideFormikActions = Pick<
  FormikActions<any>,
  'setFieldValue' | 'setErrors' | 'setSubmitting'
>

type ConnectDispatchProps = {
  authenticate: (
    data: RetailAuthenticationBody,
    slideFormikActions: SlideFormikActions,
  ) => void
  fetchPushNotificationToken: (data: RetailFetchTokenBody) => void
  setActiveScreen: (screen: Screens) => void
}

type PushNotificationOwnProps = {
  values: {
    phone: Phone
    password: string
  }
  setFieldValue: SlideFormikActions['setFieldValue']
  setErrors: SlideFormikActions['setErrors']
  setSubmitting: SlideFormikActions['setSubmitting']
  onBack: () => void
}

type PushNotificationConnectedProps = {
  pushNotificationToken: PushNotificationToken
  pushNotificationTokenFetchState: FetchStates
} & ConnectDispatchProps

type PushNotificationProps = PushNotificationOwnProps & PushNotificationConnectedProps

const PushNotification: React.FC<PushNotificationProps> = ({
  pushNotificationToken,
  pushNotificationTokenFetchState,
  values,
  authenticate,
  fetchPushNotificationToken,
  setActiveScreen,
  setFieldValue,
  setErrors,
  setSubmitting,
  ...rest
}) => {
  const formattedPhone = `${values.phone.code}${values.phone.number}`
  const tokenExpiresIn = pushNotificationToken.expiresIn / 1000

  const { timeLeft } = useCountdown(tokenExpiresIn)

  const handleOnUseDifferentMethod = useCallback(() => {
    authenticate(
      {
        phone: formattedPhone,
        password: values.password,
        type: 'SMS',
      },
      {
        setFieldValue,
        setErrors,
        setSubmitting,
      },
    )
  }, [
    formattedPhone,
    values.password,
    authenticate,
    setFieldValue,
    setErrors,
    setSubmitting,
  ])

  useEffect(() => {
    if (
      pushNotificationTokenFetchState !== FetchStates.NOT_STARTED &&
      pushNotificationTokenFetchState !== FetchStates.FAILURE
    ) {
      return noop
    }

    const timerId = setTimeout(() => {
      fetchPushNotificationToken({
        phone: formattedPhone,
        password: values.password,
        tokenId: pushNotificationToken.tokenId,
      })
    }, TOKEN_REFETCH_INTERVAL_MS)

    return () => {
      clearTimeout(timerId)
    }
  }, [
    pushNotificationTokenFetchState,
    formattedPhone,
    values.password,
    pushNotificationToken.tokenId,
    fetchPushNotificationToken,
  ])

  return (
    <Slide title='Authenticate with the app' {...rest}>
      <Flex flex={1} alignItems='center'>
        <Box>
          <CountdownCircle secondsAmount={tokenExpiresIn} timeLeft={timeLeft} />

          <TextBox mt='16px' color='grey-50'>
            We sent a notification to {formattedPhone} to authenticate your log in. Please
            make sure your Revolut app is up to date (version {MIN_APP_VERSION} or later).
          </TextBox>

          <DifferentMethod onUseDifferentMethod={handleOnUseDifferentMethod} />
        </Box>
      </Flex>
    </Slide>
  )
}

const mapStateToProps = ({ general }: AppState) => ({
  pushNotificationToken: general.data.pushNotificationToken!,
  pushNotificationTokenFetchState: general.data.pushNotificationTokenFetchState,
})

const mapDispatchToProps = (dispatch: Dispatch<any>): ConnectDispatchProps => ({
  authenticate: (
    data: RetailAuthenticationBody,
    slideFormikActions: SlideFormikActions,
  ) => dispatch(authenticateAction(data, slideFormikActions)),
  fetchPushNotificationToken: (data: RetailFetchTokenBody) =>
    dispatch(fetchPushNotificationTokenAction(data)),
  setActiveScreen: (screen: Screens) => dispatch(setActiveScreenAction(screen)),
})

const ConnectedPushNotification = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PushNotification)

export { ConnectedPushNotification as PushNotification }
